import { useRef, useState } from "react";
import { useReactFlow } from "@xyflow/react";
import { useWorkflowContext } from "../../WorkflowContext";
import useWorkflowUtils from "../Utils/WorkflowUtils";
import useLayoutUtils from "../Utils/LayoutUtils";
import axios from "axios";
import defaultContainers from './../../assets/default-containers.json' with { type: 'json' };


export function UploadWorkflow(props) {
    const fileInputRef = useRef(null);
    const { fitView } = useReactFlow()
    const { getLayoutedElements } = useWorkflowContext()
    const { parseInvoke } = useWorkflowUtils()
    const { createNodeObject, createEdgeObject} = useLayoutUtils()
    const [urlError, setUrlError] = useState(false);
    const [uploadError, setUploadError] = useState("");
    
    const shouldBuildGraphRef = useRef(false);

    const setUploadPopupEnabled = props.setUploadPopupEnabled;

    const clearUrlError = () => {
        setUrlError(false);
    }

    const clearUploadError = () => {
        setUploadError("");
    }

    const uploadFromURL = async (url) => {
        let rawURL;
        
        if (url.includes("raw.githubusercontent.com")) {
            rawURL = url;
        } else if (url.includes("github.com")) {
            // Convert regular GitHub URL to raw URL
            rawURL = url
                .replace("github.com", "raw.githubusercontent.com")
                .replace("/blob/", "/refs/heads/");
        } else {
            console.error("Invalid GitHub URL");
            setUrlError(true);
            return;
        }

        (async() => {
            const res = await axios.get(rawURL)

            const new_workflow = res.data;

                // Set flag before updating state
                shouldBuildGraphRef.current = true;

                // Update workflow and populate with defaults if missing
                buildGraph({ ...new_workflow,
                            ActionList : new_workflow.ActionList || {},
                            ComputeServers : new_workflow.ComputeServers || {},
                            DataStores : new_workflow.DataStores || {},
                            ActionContainers : new_workflow.ActionContainers || {},
                            FunctionInvoke : new_workflow.FunctionInvoke || "None",
                            DefaultDataStore : new_workflow.DefaultDataStore || "None",
                            FunctionGitRepo : new_workflow.FunctionGitRepo || {},
                            FunctionCRANPackage : new_workflow.FunctionCRANPackage || {},
                            FunctionGitHubPackage : new_workflow.FunctionGitHubPackage || {},
                            FaaSrLog : new_workflow.FaaSrLog || "FaaSrLog",
                            LoggingDataStore : new_workflow.LoggingDataStore || "",
                            InvocationID : new_workflow.InvocationID || "",
                            PyPIPackageDownloads : new_workflow.PyPIPackageDownloads || {},
                            WorkflowName : new_workflow.WorkflowName || "unnamed-workflow"
                });
                setUploadPopupEnabled(false)
        })()
    }

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const new_workflow = JSON.parse(e.target.result);
                

                // Set flag before updating state
                shouldBuildGraphRef.current = true;

                // Update workflow and populate with defaults if missing
                buildGraph({ ...new_workflow,
                            ActionList : new_workflow.ActionList || {},
                            ComputeServers : new_workflow.ComputeServers || {},
                            DataStores : new_workflow.DataStores || {},
                            ActionContainers : new_workflow.ActionContainers || {},
                            FunctionInvoke : new_workflow.FunctionInvoke || "None",
                            DefaultDataStore : new_workflow.DefaultDataStore || "None",
                            FunctionGitRepo : new_workflow.FunctionGitRepo || {},
                            FunctionCRANPackage : new_workflow.FunctionCRANPackage || {},
                            FunctionGitHubPackage : new_workflow.FunctionGitHubPackage || {},
                            FaaSrLog : new_workflow.FaaSrLog || "",
                            LoggingDataStore : new_workflow.LoggingDataStore || "",
                            InvocationID : new_workflow.InvocationID || "",
                            PyPIPackageDownloads : new_workflow.PyPIPackageDownloads || {},
                            WorkflowName : new_workflow.WorkflowName || "unnamed-workflow"
                });
                setUploadPopupEnabled(false)
            } catch (err) {
                console.error("Invalid JSON file", err);
                setUploadError(`Unable to parse the file as JSON:\n\n ${err}`);
            }
        };

        reader.readAsText(file);
    };

    const openFileDialog = () => {
        setUploadError("");
        fileInputRef.current.click();
    };

    
    const buildGraph = (newWorkflow) => {
        if (shouldBuildGraphRef.current) {
            shouldBuildGraphRef.current = false;

            const actions = newWorkflow.ActionList || {};
            let offset = 0;


            const updatedActionList = {};
            for (const key in actions) {
                const fn = actions[key];
                updatedActionList[key] = {
                    ...fn,
                    InvokeNext: fn.InvokeNext ? (typeof fn.InvokeNext[0] === 'object' ? [{ True: fn.InvokeNext[0].True || [], False: fn.InvokeNext[0].False || [] }, ...fn.InvokeNext.slice(1)] : [{ True: [], False: [] }, ...fn.InvokeNext])
                        : [{ True: [], False: [] }],
                    Arguments : fn.Arguments ? fn.Arguments : {}
                };
            }

            const defaultContainerNames = Object.values(defaultContainers['R']).concat(Object.values(defaultContainers['Python']))
            let updatedActionContainers = newWorkflow.ActionContainers
            if (newWorkflow.ActionContainers) {
                for (const actionId in updatedActionContainers) {
                    if (defaultContainerNames.some(name => name === newWorkflow.ActionContainers[actionId])){
                        updatedActionContainers[actionId] = "";
                    }
                }
            
            } 



            const updatedWorkflow = {
                ...newWorkflow,
                ActionList: updatedActionList,
                ActionContainers: updatedActionContainers
            };


            //Create Graph
            let newNodes = [];
            let newEdges = [];
            for (let action in updatedActionList) {
                newNodes.push(createNodeObject(action))
                updatedActionList[action].InvokeNext[0].True.forEach( (invoke) => {
                    const {id, rank} = parseInvoke(invoke)
                    newEdges.push({...createEdgeObject(action, id, { color : "var(--edge-true-color)", thickness : rank > 1 ? 2 : 1, label : rank > 1 ? rank : ""})});
                });
                updatedActionList[action].InvokeNext[0].False.forEach( (invoke) => {
                    const {id, rank} = parseInvoke(invoke)
                    newEdges.push(createEdgeObject(action, id, { color : "var(--edge-false-color)", thickness : rank > 1 ? 2 : 1, label : rank > 1 ? rank : ""}));
                });
                updatedActionList[action].InvokeNext.slice(1).forEach( (invoke) => {
                    const {id, rank} = parseInvoke(invoke)
                    newEdges.push(createEdgeObject(action, id, { color : "var(--edge-color)", thickness : rank > 1 ? 2 : 1, label : rank > 1 ? rank : ""}));
                });
                offset++;
            }
            for ( let edge of newEdges){
                newNodes = newNodes.map( node => {
                    return {
                        ...node,
                        data : {
                            ...node.data,
                            rank : ( edge.target === node.id) ? (edge.label === "" ? 1 : edge.label) : node.data.rank
                        }
                    }
                })
            }

            
            
            const layouted = getLayoutedElements(newNodes, newEdges, 'TB' );
            props.updateWorkflowAndLayout(updatedWorkflow, layouted.nodes, layouted.edges);

            fitView()


        }
    }; 

    return (
        <>
            { uploadError !== "" ? 
                <p className="error-text">{uploadError}</p> :
                <></>
            }
            <button onClick={openFileDialog}>Upload workflow file</button>
            <div>
                { urlError ? <p className="error-text">Invalid GitHub URL</p>:<></> }
                <input type="text" 
                    id="workflowUrlInput" 
                    placeholder="https://github.com/FaaSr/FaaSr-workflow/tutorial.json"
                    onChange={clearUrlError}
                    onBlur={clearUrlError}
                    onClick={clearUrlError}
                ></input>
                <button onClick={() => uploadFromURL(document.getElementById("workflowUrlInput").value)}>Import from Github URL</button>
            </div>
            <input
                type="file"
                accept=".json"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </>
    );
}
